/**
 * What one LinkedIn sender of a company can do with one lead right now (PREC-4216). The API answers
 * it per sender (`users/check-connection` with a `listPersonId`), the composer reads it, and the
 * schedule path re-asks it at commit time — so all three use this one classification.
 *
 * - `CONNECTED`: a first-degree connection (the connection table, or this sender's invitation was
 *   accepted). An ordinary message is delivered.
 * - `CHAT`: no connection, but a chat with the lead exists on this sender's account (the lead wrote
 *   back to the invite note, PREC-4221). An ordinary message is delivered into that chat.
 * - `INVITATION_PENDING`: this sender's own invitation is open (not accepted, not withdrawn, not past
 *   its deadline) and there is no chat. An ordinary message can only fail; the message waits behind
 *   the invitation instead.
 * - `NONE`: nothing of the above. The composer offers an invitation.
 *
 * An invitation-only conversation (an ACTION row) is never a chat, and a colleague's invitation or
 * chat never counts for this sender.
 */
export const LinkedinSenderState = {
	CONNECTED: "CONNECTED",
	CHAT: "CHAT",
	INVITATION_PENDING: "INVITATION_PENDING",
	NONE: "NONE",
} as const;

export type LinkedinSenderState = (typeof LinkedinSenderState)[keyof typeof LinkedinSenderState];

export const LINKEDIN_SENDER_STATES: readonly LinkedinSenderState[] =
	Object.values(LinkedinSenderState);

/** The evidence the classification decides on, gathered per sender. */
export interface LinkedinSenderEvidence {
	/** A connection row, or this sender's invitation to the lead was accepted. */
	connected: boolean;
	/** A chat with the lead on this sender's account (a MESSAGE row with a chat id). */
	hasChat: boolean;
	/** This sender's own invitation to the lead is open. */
	invitationPending: boolean;
}

/** Strongest evidence wins: a connection over a chat over a pending invitation. */
export function classifyLinkedinSenderState(evidence: LinkedinSenderEvidence): LinkedinSenderState {
	if (evidence.connected) {
		return LinkedinSenderState.CONNECTED;
	}

	if (evidence.hasChat) {
		return LinkedinSenderState.CHAT;
	}

	if (evidence.invitationPending) {
		return LinkedinSenderState.INVITATION_PENDING;
	}

	return LinkedinSenderState.NONE;
}

/**
 * Whether an ordinary LinkedIn message from this sender is delivered now. Total over the states, so
 * a new state is a type error here rather than a silent "yes".
 */
export const LINKEDIN_SENDER_CAN_MESSAGE: Record<LinkedinSenderState, boolean> = {
	[LinkedinSenderState.CONNECTED]: true,
	[LinkedinSenderState.CHAT]: true,
	[LinkedinSenderState.INVITATION_PENDING]: false,
	[LinkedinSenderState.NONE]: false,
};

/** Error code of a refused ordinary message to a lead whose invitation from this sender is open. */
export const LINKEDIN_INVITATION_PENDING_ERROR = "LINKEDIN_INVITATION_PENDING";

/** Error code of a second identical message parked behind the same invitation within the window. */
export const PARKED_MESSAGE_DUPLICATE_ERROR = "PARKED_MESSAGE_DUPLICATE";

/** How long an identical parked message behind the same invitation counts as a double submit. */
export const PARKED_MESSAGE_DEDUPE_WINDOW_MS = 2 * 60 * 1000;
